import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE } from '@cashier/cashier-common';
import { cn } from '@cashier/cashier-ui';

import { useConfirmationForm, useFormSchema } from '../hooks';
import { FieldWrapper } from '../modules/fieldWrapper';
import { FileUploadingField } from '../modules/fileUploadingField';
import { FormAction } from '../modules/formAction/v2';
import { InformationFieldList } from '../modules/informationFieldList';

import styles from './styles.module.scss';

interface ConfirmationFormProps {
  version: 'v2' | 'v3';
}

export const ConfirmationForm = ({
  version,
}: ConfirmationFormProps): ReactElement => {
  const { t } = useTranslation();
  const { methodName, onSubmit, onCancel } = useConfirmationForm();

  const schema = useFormSchema();

  const { informationFieldMap, commonFieldMap, fileUploadField } = schema || {};

  return (
    <div className={styles.flexWrapper}>
      <div
        className={cn({
          [styles.content]: true,
          [styles.contentV3]: version === 'v3',
        })}
      >
        <div
          className={cn({
            [styles.title]: true,
            [styles.titleV3]: version === 'v3',
          })}
          data-test="confirmation-form-title"
        >
          {t('secondStep.heading', { method: methodName })}
        </div>
        <div
          className={cn({
            [styles.text]: true,
            [styles.textV3]: version === 'v3',
          })}
          data-test="confirmation-form-text"
          dangerouslySetInnerHTML={{ __html: t('secondStep.message') }}
        />

        {informationFieldMap && (
          <InformationFieldList fields={Object.values(informationFieldMap)} />
        )}

        {commonFieldMap &&
          Object.entries(commonFieldMap).map(([key, field]) => (
            <FieldWrapper key={key} field={field} version={version} />
          ))}

        {fileUploadField && (
          <FileUploadingField
            reference={fileUploadField.name}
            version={version}
            acceptTypes={ACCEPTED_FILE_TYPES}
            maxSize={MAX_FILE_SIZE}
          />
        )}

        {version !== 'v3' && (
          <FormAction onSubmit={onSubmit} onCancel={onCancel} />
        )}
      </div>
    </div>
  );
};
